import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { broadcastEntityChange } from "@/lib/useEntityRefresh";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tags, Search, MapPin, ArrowLeft, ChevronRight, Loader2, X, History, Calendar, Image as ImageIcon, Package, Sparkles, List, Map as MapIcon } from "lucide-react";
import BulkFindEspaciosModal from "@/components/BulkFindEspaciosModal";
import EspaciosMap from "@/components/EspaciosMap";
import GroupSortControls from "@/components/GroupSortControls";
import { useTableSort } from "@/lib/useTableSort";

const empty = { nombre: "", color: "#f59e0b", logo_url: "", notas: "", aliases_txt: "" };

export default function CadenasPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [globalResults, setGlobalResults] = useState(null);  // null = no buscando, [] = buscando vacío
  const [searching, setSearching] = useState(false);
  const [detailCadena, setDetailCadena] = useState(null); // cadena seleccionada para vista detalle
  const [viewMode, setViewMode] = useState("list"); // "list" | "map"
  const [allEspacios, setAllEspacios] = useState([]); // para el mapa global
  const [loadingMap, setLoadingMap] = useState(false);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("dnf-cadenas-sortby") || "nombre");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("dnf-cadenas-sortdir") || "asc");
  useEffect(() => { localStorage.setItem("dnf-cadenas-sortby", sortBy); }, [sortBy]);
  useEffect(() => { localStorage.setItem("dnf-cadenas-sortdir", sortDir); }, [sortDir]);
  const itemsSorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "nombre": va = (a.nombre || "").toLowerCase(); vb = (b.nombre || "").toLowerCase(); break;
        case "created_at": va = a.created_at || ""; vb = b.created_at || ""; break;
        default: va = (a.nombre || "").toLowerCase(); vb = (b.nombre || "").toLowerCase();
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [items, sortBy, sortDir]);

  const load = useCallback(() => api.get("/cadenas").then((r) => setItems(r.data || [])), []);
  useEffect(() => { load(); }, [load]);

  // Cargar todos los espacios al entrar al modo Mapa (lazy)
  useEffect(() => {
    if (viewMode !== "map" || allEspacios.length > 0) return;
    setLoadingMap(true);
    api.get("/espacios", { params: { limit: 5000 } })
      .then((r) => setAllEspacios(r.data || []))
      .finally(() => setLoadingMap(false));
  }, [viewMode, allEspacios.length]);

  // Búsqueda global: cuando hay texto, se hace contra /api/espacios?search=...
  useEffect(() => {
    if (!search.trim()) { setGlobalResults(null); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/espacios", { params: { search, limit: 200 } });
        if (!cancelled) setGlobalResults(data || []);
      } finally { if (!cancelled) setSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const cadenaName = (id) => items.find((c) => c.id === id)?.nombre || "—";

  // Sort headers para tabla de resultados globales
  const globalSort = useTableSort(globalResults || [], { by: "nombre", dir: "asc" });

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, aliases_txt: (c.aliases || []).join(", ") }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const { aliases_txt, ...payload } = form;
    payload.aliases = aliases_txt.split(",").map((s) => s.trim()).filter(Boolean);
    Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
    if (editing) await api.patch(`/cadenas/${editing.id}`, payload);
    else await api.post("/cadenas", payload);
    broadcastEntityChange("cadena", { action: editing ? "update" : "create" });
    setOpen(false); load();
  };

  // === DETAIL VIEW (cuando seleccionas una cadena) ===
  if (detailCadena) {
    return <CadenaDetail cadena={detailCadena}
                          onBack={() => setDetailCadena(null)} />;
  }

  return (
    <div className="p-8 max-w-[1800px] space-y-6">
      <PageHeader
        title="Cadenas"
        subtitle="Marcas/cadenas de los puntos de venta (Aromas, Primor, ECI, Douglas, Druni, Carrefour...). Pulsa una cadena para ver sus espacios."
        action={
          <Button onClick={openCreate} data-testid="new-cadena-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Nueva cadena
          </Button>
        }
      />

      {/* Buscador global */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Buscar espacio, dirección, ciudad, provincia, CP, código PDV..."
               data-testid="cadena-search-global"
               className="bg-zinc-900 border-zinc-800 pl-10 pr-10" />
        {search && (
          <button onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Resultados de búsqueda global */}
      {globalResults !== null && (
        <div className="border border-zinc-800 rounded-lg overflow-hidden">
          <div className="bg-zinc-900/60 px-4 py-2 flex items-center justify-between border-b border-zinc-800">
            <div className="text-xs uppercase tracking-wider text-zinc-400">
              {searching ? <span className="flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Buscando…</span>
                         : <>Resultados: <b className="text-amber-400">{globalResults.length}</b> espacios</>}
            </div>
          </div>
          {globalResults.length === 0 && !searching ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Sin coincidencias.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/40 text-zinc-500 text-[10px] uppercase select-none">
                <tr>
                  <SortableTh label="Nombre" field="nombre"
                              requestSort={globalSort.requestSort} icon={globalSort.sortIcon} active={globalSort.sortBy} />
                  <SortableTh label="Cadena" field="cadena_id"
                              requestSort={globalSort.requestSort} icon={globalSort.sortIcon} active={globalSort.sortBy} />
                  <SortableTh label="Ciudad" field="ciudad"
                              requestSort={globalSort.requestSort} icon={globalSort.sortIcon} active={globalSort.sortBy} />
                  <SortableTh label="Provincia" field="provincia" width="w-24"
                              requestSort={globalSort.requestSort} icon={globalSort.sortIcon} active={globalSort.sortBy} />
                  <SortableTh label="CP" field="cp" width="w-20"
                              requestSort={globalSort.requestSort} icon={globalSort.sortIcon} active={globalSort.sortBy} />
                  <SortableTh label="Código PDV" field="codigo_pdv" width="w-32"
                              requestSort={globalSort.requestSort} icon={globalSort.sortIcon} active={globalSort.sortBy} />
                </tr>
              </thead>
              <tbody>
                {globalSort.sorted.map((e) => (
                  <tr key={e.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40">
                    <td className="p-2.5">
                      <div className="font-medium text-zinc-100">{e.nombre}</div>
                      {e.direccion && <div className="text-[11px] text-zinc-500">{e.direccion}</div>}
                    </td>
                    <td className="p-2.5 text-amber-400 text-xs">{cadenaName(e.cadena_id)}</td>
                    <td className="p-2.5 text-zinc-300 text-xs">{e.ciudad || "—"}</td>
                    <td className="p-2.5 text-zinc-400 text-xs">{e.provincia || "—"}</td>
                    <td className="p-2.5 text-zinc-400 font-mono text-xs">{e.cp || "—"}</td>
                    <td className="p-2.5 text-zinc-400 font-mono text-[11px]">{e.codigo_pdv || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Toggle vista lista/mapa */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500">
          {items.length} {items.length === 1 ? "cadena" : "cadenas"} registradas
        </div>
        <div className="inline-flex rounded-md border border-zinc-800 bg-zinc-900/70 p-0.5" data-testid="cadenas-view-toggle">
          <button
            type="button"
            onClick={() => setViewMode("list")}
            data-testid="cadenas-view-list"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition ${
              viewMode === "list"
                ? "bg-amber-500 text-zinc-900 font-semibold"
                : "text-zinc-400 hover:text-zinc-100"
            }`}
          >
            <List className="w-3.5 h-3.5" /> Lista
          </button>
          <button
            type="button"
            onClick={() => setViewMode("map")}
            data-testid="cadenas-view-map"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded transition ${
              viewMode === "map"
                ? "bg-amber-500 text-zinc-900 font-semibold"
                : "text-zinc-400 hover:text-zinc-100"
            }`}
          >
            <MapIcon className="w-3.5 h-3.5" /> Mapa global
          </button>
        </div>
      </div>

      {viewMode === "map" ? (
        loadingMap ? (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando espacios…
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-[11px] text-zinc-500">
              Mostrando <b className="text-amber-400">{allEspacios.filter((e) => typeof e.latitud === "number" && typeof e.longitud === "number").length}</b> espacios geolocalizados de {allEspacios.length} totales. Colores por cadena.
            </div>
            <EspaciosMap espacios={allEspacios} cadenas={items} height={640} />
          </div>
        )
      ) : (
      <>
      {/* Ordenar */}
      <div className="flex items-center justify-between mb-4">
        <GroupSortControls
          testIdPrefix="cadenas"
          groupBy={"none"} setGroupBy={() => {}}
          groupOptions={[]}
          sortBy={sortBy} setSortBy={setSortBy}
          sortOptions={[
            { value: "nombre", label: "Nombre" },
            { value: "created_at", label: "Fecha de creación" },
          ]}
          sortDir={sortDir} setSortDir={setSortDir}
          onReset={() => { setSortBy("nombre"); setSortDir("asc"); }}
        />
        <div className="text-[10px] text-zinc-500" data-testid="cadenas-count">
          {itemsSorted.length} {itemsSorted.length === 1 ? "cadena" : "cadenas"}
        </div>
      </div>
      {/* Grid cadenas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {itemsSorted.map((c) => (
          <div key={c.id} data-testid={`cadena-card-${c.id}`}
               className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/40 transition group">
            <button onClick={() => setDetailCadena(c)}
                    data-testid={`cadena-open-${c.id}`}
                    className="w-full text-left">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-zinc-900 font-bold text-sm"
                     style={{ backgroundColor: c.color || "#f59e0b" }}>
                  <Tags className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-semibold truncate">{c.nombre}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{c.color}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-amber-400 transition" />
              </div>
              {c.notas && <div className="text-xs text-zinc-400 mb-3 line-clamp-2">{c.notas}</div>}
            </button>
            <div className="flex gap-2 pt-3 border-t border-zinc-800">
              <Button size="sm" variant="ghost" onClick={() => openEdit(c)}
                      className="text-zinc-400 hover:text-amber-300 h-7 px-2">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
              <Button size="sm" variant="ghost"
                      onClick={async () => { if (window.confirm(`¿Eliminar cadena ${c.nombre}?`)) { await api.delete(`/cadenas/${c.id}`); load(); } }}
                      className="text-zinc-500 hover:text-red-400 h-7 px-2">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar cadena" : "Nueva cadena"}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Configura la marca paraguas que agrupa puntos de venta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nombre *</Label>
                <Input required value={form.nombre} data-testid="form-cadena-nombre"
                       onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Color (hex)</Label>
                <div className="flex gap-2">
                  <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                         className="bg-zinc-900 border-zinc-800 flex-1" />
                  <input type="color" value={form.color || "#f59e0b"}
                         onChange={(e) => setForm({ ...form, color: e.target.value })}
                         className="w-12 h-10 rounded border border-zinc-700 bg-zinc-900 cursor-pointer" />
                </div></div>
              <div><Label>Logo de la cadena</Label>
                <div className="flex items-center gap-2">
                  {form.logo_url && (
                    <img src={form.logo_url} alt="logo" className="w-10 h-10 rounded bg-white p-0.5 object-contain border border-zinc-700" />
                  )}
                  <Input value={form.logo_url} placeholder="https://..." onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="bg-zinc-900 border-zinc-800 flex-1" />
                  <label className="cursor-pointer bg-amber-500 hover:bg-amber-400 text-zinc-900 text-xs font-semibold px-3 py-2 rounded inline-flex items-center gap-1" data-testid="cadena-logo-upload">
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          toast.info("Subiendo logo...");
                          const { secure_url } = await uploadToCloudinary(file, "dnf/cadenas/logos", "image");
                          setForm({ ...form, logo_url: secure_url });
                          toast.success("Logo subido");
                        } catch (err) {
                          toast.error("Error al subir: " + (err.message || err));
                        } finally {
                          e.target.value = "";
                        }
                      }} />
                    Subir
                  </label>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">Aparecerá en presupuestos, portal cliente y emails. PNG con fondo transparente recomendado.</div>
              </div>
              <div className="col-span-2"><Label>Alias en correos</Label>
                <Input value={form.aliases_txt} onChange={(e) => setForm({ ...form, aliases_txt: e.target.value })}
                       placeholder="ECI, Corte Ingles, Cortes Ingleses" data-testid="form-cadena-aliases"
                       className="bg-zinc-900 border-zinc-800" />
                <div className="text-[10px] text-zinc-500 mt-1">Separados por comas. Se usan para reconocer la enseña en el asunto/filas de los correos (Crear desde email).</div>
              </div>
              <div className="col-span-2"><Label>Notas</Label>
                <Textarea value={form.notas} rows={2} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                          className="bg-zinc-900 border-zinc-800" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
              <Button type="submit" data-testid="form-cadena-submit" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ CADENA DETAIL ============
function CadenaDetail({ cadena, onBack }) {
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [provinciaFilter, setProvinciaFilter] = useState("");
  const [historial, setHistorial] = useState(null);   // espacio seleccionado para historial
  const [editEspacio, setEditEspacio] = useState(null); // espacio seleccionado para editar (inline)
  const [bulkFindOpen, setBulkFindOpen] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return api.get(`/cadenas/${cadena.id}/resumen`)
      .then((r) => setResumen(r.data))
      .finally(() => setLoading(false));
  }, [cadena.id]);

  useEffect(() => { reload(); }, [reload]);

  // Filtros locales
  const filtered = useMemo(() => {
    if (!resumen) return [];
    const s = search.trim().toLowerCase();
    return resumen.espacios.filter((e) => {
      if (provinciaFilter && (e.provincia || "") !== provinciaFilter) return false;
      if (!s) return true;
      return [e.nombre, e.direccion, e.ciudad, e.provincia, e.cp, e.codigo_pdv, e.cliente_nombre]
        .some((v) => (v || "").toLowerCase().includes(s));
    });
  }, [resumen, search, provinciaFilter]);

  // Ordenación tipo hoja de cálculo (click cabecera: asc → desc → off)
  const { sorted, requestSort, sortIcon, sortBy: tSortBy } = useTableSort(filtered, { by: "nombre", dir: "asc" });

  return (
    <div className="p-8 max-w-[1800px] space-y-5" data-testid="cadena-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack}
                  data-testid="cadena-detail-back"
                  className="text-zinc-400 hover:text-zinc-100">
            <ArrowLeft className="w-4 h-4 mr-1" /> Cadenas
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center text-zinc-900 font-bold"
                 style={{ backgroundColor: cadena.color || "#f59e0b" }}>
              <Tags className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{cadena.nombre}</h1>
              <p className="text-xs text-zinc-500">
                {loading ? "Cargando…" : `${resumen?.total_espacios ?? 0} espacios · ${resumen?.provincias?.length ?? 0} provincias`}
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setBulkFindOpen(true)}
          data-testid="cadena-bulk-find-btn"
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-zinc-900 font-semibold shadow-lg shadow-amber-500/20"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Buscar tiendas con Google
        </Button>
      </div>

      <BulkFindEspaciosModal
        open={bulkFindOpen}
        onOpenChange={setBulkFindOpen}
        cadena={cadena}
        defaultTipo="Perfumería"
        onImported={reload}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : (
        <>
          {/* KPI provincias top */}
          {resumen.provincias?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setProvinciaFilter("")}
                      className={`px-3 py-1 rounded-full text-xs border transition ${provinciaFilter === "" ? "bg-amber-500 text-zinc-900 border-amber-500 font-semibold" : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-amber-500"}`}>
                Todas ({resumen.total_espacios})
              </button>
              {resumen.provincias.map((p) => (
                <button key={p.nombre} onClick={() => setProvinciaFilter(p.nombre)}
                        data-testid={`cadena-prov-${p.nombre}`}
                        className={`px-3 py-1 rounded-full text-xs border transition ${provinciaFilter === p.nombre ? "bg-amber-500 text-zinc-900 border-amber-500 font-semibold" : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-amber-500"}`}>
                  {p.nombre} <span className="opacity-70">·{p.count}</span>
                </button>
              ))}
            </div>
          )}

          {/* Buscador dentro de la cadena */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Filtrar por nombre, dirección, ciudad, CP, código PDV..."
                   data-testid="cadena-detail-search"
                   className="bg-zinc-900 border-zinc-800 pl-10" />
          </div>

          {/* Tabla de espacios */}
          <div className="border border-zinc-800 rounded-lg overflow-hidden">
            {filtered.length === 0 ? (              <div className="p-12 text-center text-zinc-500 text-sm">
                <MapPin className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
                {resumen.espacios.length === 0
                  ? "Esta cadena aún no tiene espacios asociados."
                  : "Ningún espacio coincide con los filtros."}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-900/40 text-zinc-500 text-[10px] uppercase select-none">
                  <tr>
                    <SortableTh label="Nombre"   field="nombre"     requestSort={requestSort} icon={sortIcon} active={tSortBy} />
                    <SortableTh label="Dirección" field="direccion" requestSort={requestSort} icon={sortIcon} active={tSortBy} />
                    <SortableTh label="Ciudad"    field="ciudad"    width="w-24" requestSort={requestSort} icon={sortIcon} active={tSortBy} />
                    <SortableTh label="Provincia" field="provincia" width="w-24" requestSort={requestSort} icon={sortIcon} active={tSortBy} />
                    <SortableTh label="CP"        field="cp"        width="w-20" requestSort={requestSort} icon={sortIcon} active={tSortBy} />
                    <SortableTh label="Código PDV" field="codigo_pdv" width="w-28" requestSort={requestSort} icon={sortIcon} active={tSortBy} />
                    <th className="text-right p-2.5 w-20">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((e) => (
                    <tr key={e.id}
                        className="border-t border-zinc-800/60 hover:bg-zinc-900/40"
                        data-testid={`cadena-esp-${e.id}`}>
                      <td className="p-2.5 font-medium text-zinc-100 cursor-pointer"
                          onClick={() => setHistorial(e)}>
                        <div className="flex items-center gap-2">
                          <History className="w-3.5 h-3.5 text-zinc-600" />
                          {e.nombre}
                        </div>
                      </td>
                      <td className="p-2.5 text-zinc-300 text-xs cursor-pointer" onClick={() => setHistorial(e)}>{e.direccion || "—"}</td>
                      <td className="p-2.5 text-zinc-300 text-xs cursor-pointer" onClick={() => setHistorial(e)}>{e.ciudad || "—"}</td>
                      <td className="p-2.5 text-zinc-400 text-xs cursor-pointer" onClick={() => setHistorial(e)}>{e.provincia || "—"}</td>
                      <td className="p-2.5 text-zinc-400 font-mono text-xs cursor-pointer" onClick={() => setHistorial(e)}>{e.cp || "—"}</td>
                      <td className="p-2.5 text-zinc-400 font-mono text-[11px] cursor-pointer" onClick={() => setHistorial(e)}>{e.codigo_pdv || "—"}</td>
                      <td className="p-2.5 text-right">
                        <button
                          onClick={(ev) => { ev.stopPropagation(); setEditEspacio(e); }}
                          data-testid={`btn-edit-espacio-${e.id}`}
                          title="Editar este espacio"
                          className="text-zinc-500 hover:text-amber-300 px-2 py-1 rounded hover:bg-amber-500/10 transition"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {filtered.length > 0 && (
              <div className="bg-zinc-900/40 px-3 py-1.5 text-[11px] text-zinc-500 border-t border-zinc-800">
                Mostrando <b className="text-zinc-200">{filtered.length}</b> de {resumen.total_espacios} · click en cualquier fila para ver el historial
              </div>
            )}
          </div>
        </>
      )}

      {/* Dialog historial */}
      <EspacioHistorialDialog espacio={historial} onClose={() => setHistorial(null)} />

      {/* Dialog edición inline desde Cadenas */}
      <EspacioEditDialog
        espacio={editEspacio}
        onClose={() => setEditEspacio(null)}
        onSaved={() => { setEditEspacio(null); reload(); }}
      />
    </div>
  );
}


// ============ HISTORIAL DE MONTAJES ============
function EspacioHistorialDialog({ espacio, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [anoFilter, setAnoFilter] = useState("");

  useEffect(() => {
    if (!espacio) { setData(null); return; }
    setLoading(true);
    api.get(`/espacios/${espacio.id}/historial`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [espacio]);

  if (!espacio) return null;
  const montajes = !data ? [] : (anoFilter
    ? data.montajes.filter((m) => (m.fecha_instalacion || "").startsWith(anoFilter))
    : data.montajes);

  return (
    <Dialog open={!!espacio} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-amber-400" />
            Historial · {espacio.nombre}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {espacio.direccion ? `${espacio.direccion}, ` : ""}{espacio.ciudad}{espacio.provincia ? ` (${espacio.provincia})` : ""}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : !data || data.total_montajes === 0 ? (
          <div className="py-12 text-center text-zinc-500">
            <Package className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
            Sin montajes registrados en este espacio aún.
          </div>
        ) : (
          <>
            {/* Filtros por año */}
            <div className="flex flex-wrap gap-2 mb-3">
              <button onClick={() => setAnoFilter("")}
                      className={`px-3 py-1 rounded-full text-xs border transition ${anoFilter === "" ? "bg-amber-500 text-zinc-900 border-amber-500 font-semibold" : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-amber-500"}`}>
                Todos ({data.total_montajes})
              </button>
              {data.anos.map((a) => (
                <button key={a.ano} onClick={() => setAnoFilter(a.ano)}
                        data-testid={`hist-ano-${a.ano}`}
                        className={`px-3 py-1 rounded-full text-xs border transition ${anoFilter === a.ano ? "bg-amber-500 text-zinc-900 border-amber-500 font-semibold" : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-amber-500"}`}>
                  {a.ano} <span className="opacity-70">·{a.count}</span>
                </button>
              ))}
            </div>

            {/* Timeline */}
            <div className="space-y-3" data-testid="hist-timeline">
              {montajes.map((m) => (
                <MontajeCard key={m.linea_id} m={m} />
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MontajeCard({ m }) {
  const [expanded, setExpanded] = useState(false);
  const allPhotos = [...(m.fotos_antes || []), ...(m.fotos_despues || [])];
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg overflow-hidden">
      <button onClick={() => setExpanded((x) => !x)}
              data-testid={`hist-montaje-${m.linea_id}`}
              className="w-full text-left p-3 hover:bg-zinc-900/60 transition flex items-start gap-3">
        <div className="flex flex-col items-center w-14 flex-shrink-0">
          <Calendar className="w-4 h-4 text-amber-400" />
          <div className="text-[10px] uppercase text-zinc-500 mt-0.5 font-mono">
            {m.fecha_instalacion ? m.fecha_instalacion.slice(5) : "—"}
          </div>
          <div className="text-[11px] text-zinc-300 font-bold">
            {m.fecha_instalacion?.slice(0, 4) || ""}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div className="font-semibold text-zinc-100">
              {m.marca || m.peticion_nombre || "Sin título"}
            </div>
            <div className="text-[10px] text-amber-400 font-mono">{m.peticion_codigo}</div>
          </div>
          <div className="text-xs text-zinc-400 mt-0.5">
            {m.cliente_nombre} · {m.tipo?.replace(/_/g, " ")} · {m.estado}
          </div>
          {m.descripcion && (
            <div className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{m.descripcion}</div>
          )}
          <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-zinc-500">
            <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {m.componentes.length} componente{m.componentes.length === 1 ? "" : "s"}</span>
            <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {m.fotos_total} foto{m.fotos_total === 1 ? "" : "s"}</span>
            {m.comercial_nombre && <span>· {m.comercial_nombre}</span>}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 p-3 bg-zinc-900/30">
          {/* Componentes */}
          {m.componentes.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-amber-400/70 mb-1.5">Componentes</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs mb-3">
                  <thead className="text-zinc-500 text-[10px] uppercase">
                    <tr><th className="text-left pb-1">Nombre</th><th className="text-left">Material</th><th className="text-right w-20">Medidas</th><th className="text-right w-10">Uds</th></tr>
                  </thead>
                  <tbody>
                    {m.componentes.map((c, i) => (
                      <tr key={i} className="border-t border-zinc-800/40">
                        <td className="py-1 text-zinc-200">{c.nombre || c.descripcion || "—"}</td>
                        <td className="py-1 text-zinc-400">{c.material || "—"}</td>
                        <td className="py-1 text-right font-mono text-zinc-300">
                          {c.ancho_cm && c.alto_cm ? `${c.ancho_cm}×${c.alto_cm}` : "—"}
                        </td>
                        <td className="py-1 text-right text-zinc-300">{c.cantidad || 1}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Fotos */}
          {allPhotos.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-amber-400/70 mb-1.5">Fotos</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {m.fotos_antes.map((f) => (
                  <a key={f.id} href={f.url} target="_blank" rel="noreferrer" title="Antes"
                     className="relative aspect-square bg-zinc-950 border border-zinc-800 rounded overflow-hidden group">
                    <img src={f.url} alt="Antes" loading="lazy" className="w-full h-full object-cover" />
                    <span className="absolute top-1 left-1 text-[9px] uppercase bg-zinc-900/80 px-1 rounded text-zinc-300">Antes</span>
                  </a>
                ))}
                {m.fotos_despues.map((f) => (
                  <a key={f.id} href={f.url} target="_blank" rel="noreferrer" title="Después"
                     className="relative aspect-square bg-zinc-950 border border-emerald-900/40 rounded overflow-hidden group">
                    <img src={f.url} alt="Después" loading="lazy" className="w-full h-full object-cover" />
                    <span className="absolute top-1 left-1 text-[9px] uppercase bg-emerald-700/80 px-1 rounded text-zinc-100">Después</span>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ============ EDICIÓN INLINE DE ESPACIO (desde vista Cadena) ============
function EspacioEditDialog({ espacio, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (espacio) {
      setForm({
        nombre: espacio.nombre || "",
        direccion: espacio.direccion || "",
        ciudad: espacio.ciudad || "",
        provincia: espacio.provincia || "",
        cp: espacio.cp || "",
        codigo_pdv: espacio.codigo_pdv || "",
        codigo_interno: espacio.codigo_interno || "",
        notas: espacio.notas || "",
        contacto: espacio.contacto || "",
        telefono_local: espacio.telefono_local || "",
      });
    }
  }, [espacio]);

  if (!espacio) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/espacios/${espacio.id}`, form);
      toast.success("Espacio actualizado");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!espacio} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="dialog-edit-espacio">
        <DialogHeader>
          <DialogTitle className="text-amber-300 flex items-center gap-2">
            <Pencil className="w-4 h-4" /> Editar espacio: {espacio.nombre}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Nombre</Label>
            <Input value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              data-testid="esp-edit-nombre" required
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Dirección</Label>
            <Input value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })}
              data-testid="esp-edit-direccion"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Ciudad</Label>
            <Input value={form.ciudad || ""} onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
              data-testid="esp-edit-ciudad"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Provincia</Label>
            <Input value={form.provincia || ""} onChange={(e) => setForm({ ...form, provincia: e.target.value })}
              data-testid="esp-edit-provincia"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">CP</Label>
            <Input value={form.cp || ""} onChange={(e) => setForm({ ...form, cp: e.target.value })}
              data-testid="esp-edit-cp"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Código PDV</Label>
            <Input value={form.codigo_pdv || ""} onChange={(e) => setForm({ ...form, codigo_pdv: e.target.value })}
              data-testid="esp-edit-codigo-pdv"
              className="bg-zinc-900 border-zinc-800 font-mono" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Contacto local</Label>
            <Input value={form.contacto || ""} onChange={(e) => setForm({ ...form, contacto: e.target.value })}
              data-testid="esp-edit-contacto"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Teléfono local</Label>
            <Input value={form.telefono_local || ""} onChange={(e) => setForm({ ...form, telefono_local: e.target.value })}
              data-testid="esp-edit-telefono"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Notas</Label>
            <textarea
              value={form.notas || ""}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
              data-testid="esp-edit-notas"
              rows={2}
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100"
            />
          </div>
          <div className="col-span-2 flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button type="submit" disabled={saving}
              data-testid="esp-edit-save"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambios"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}



// ============ SortableTh ============
// Cabecera de columna clicable tipo hoja de cálculo.
function SortableTh({ label, field, width, requestSort, icon, active }) {
  const isActive = active === field;
  return (
    <th
      className={`text-left p-2.5 ${width || ""} cursor-pointer hover:bg-zinc-800/40 transition-colors ${isActive ? "text-amber-300" : ""}`}
      onClick={() => requestSort(field)}
      data-testid={`th-sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[9px] ${isActive ? "opacity-100" : "opacity-40"}`}>
          {icon(field)}
        </span>
      </span>
    </th>
  );
}
